import { useInfiniteQuery } from '@tanstack/react-query';
import { Button, Card } from '@promtver/ui-kit';

import { getAuditLog } from '../../api/admin';
import { AppShell } from '../../components/AppShell';
import { AsyncState } from '../../components/AsyncState';
import { KeyValue } from '../../components/KeyValue';
import { ru } from '../../i18n/ru';
import { formatDateTimeLocal } from '../../lib/datetime';

/**
 * Журнал аудита (ТЗ §4.3 «Аудит»): кто, что и когда — с привязкой к объекту.
 * Записи только читаются: журнал append-only (ТЗ §6.4).
 */
export function AuditPage() {
  const auditQuery = useInfiniteQuery({
    queryKey: ['admin', 'audit-log'],
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => getAuditLog(pageParam),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
  });

  const entries = auditQuery.data?.pages.flatMap((page) => page.items) ?? [];

  return (
    <AppShell title={ru.audit.title} subtitle={ru.audit.subtitle}>
      <Card title={ru.audit.title}>
        <AsyncState
          isLoading={auditQuery.isLoading}
          isError={auditQuery.isError}
          isEmpty={entries.length === 0}
          emptyTitle={ru.audit.empty}
          onRetry={() => void auditQuery.refetch()}
        >
          <ul className="flex flex-col gap-2">
            {entries.map((entry) => (
              <li key={entry.id} className="rounded-card border border-line p-3">
                <div className="flex flex-col gap-1">
                  <KeyValue label={ru.audit.actor} value={entry.actorName} />
                  <KeyValue label={ru.audit.action} value={entry.action} mono />
                  <KeyValue
                    label={ru.audit.entity}
                    value={`${entry.entityType}: ${entry.entityLabel ?? entry.entityId}`}
                    mono
                  />
                  <KeyValue label={ru.audit.time} value={formatDateTimeLocal(entry.createdAt)} />
                  {entry.meta ? (
                    <KeyValue
                      label={ru.audit.meta}
                      value={JSON.stringify(entry.meta)}
                      mono
                    />
                  ) : null}
                </div>
              </li>
            ))}
          </ul>

          {auditQuery.hasNextPage ? (
            <div className="mt-3">
              <Button
                variant="outline"
                disabled={auditQuery.isFetchingNextPage}
                onClick={() => void auditQuery.fetchNextPage()}
              >
                {ru.common.more}
              </Button>
            </div>
          ) : null}
        </AsyncState>
      </Card>
    </AppShell>
  );
}
